import { Button, Col, Flex, Radio, Row, Space, Tooltip, Checkbox, Alert } from "antd";
import { WysiwygDisplay } from "./WysiwygDisplay";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { ReactComponent as Right } from "../assets/icons/Right.svg";
import { ReactComponent as Left } from "../assets/icons/Left.svg";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { saveProgressAsync, selectCourse } from "../features/courses/coursesSlice";
import { useParams } from "react-router-dom";
import { TextToSpeech } from "./Text-To-Speech";
import { CourseResult } from "./CourseResult";
import { regenerateShuffleKey } from "../features/courses/coursesAPI";
import useInactivity from "../hooks/useInactivity";

export const CourseQuiz = ({ lesson, onNext, current }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const data = useSelector((state) => selectCourse(state));
    const [questionStates, setQuestionStates] = useState({});
    const [quizAnswers, setQuizAnswers] = useState({});
    const [activeQuestion, setActiveQuestion] = useState(0);
    const [shuffledQuestions, setShuffledQuestions] = useState([]); // State for storing shuffled questions
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [previousAttemptAnswers, setPreviousAttemptAnswers] = useState({});
    const [showAutoNavigateMessage, setShowAutoNavigateMessage] = useState(false);
    const [autoNavigateTimeout, setAutoNavigateTimeout] = useState(null);
    const [countdown, setCountdown] = useState(5);
    const { isInactive, isTabInactive } = useInactivity();

    useEffect(() => {
        if (isInactive || isTabInactive) {
            if ("Notification" in window && Notification.permission === "granted") {
                new Notification("You have an in-progress quiz", {
                    body: "Please return to complete your quiz.",
                    // icon: "/path/to/icon.png", // Optional: Add an icon for the notification
                });
            }
        }
    }, [isInactive, isTabInactive]);

    // Memoize the shuffled questions
    const memoizedShuffledQuestions = useMemo(() => {
        if (lesson?.questions) {
            // Shuffle the questions and their answers
            return lesson.questions.map((question) => ({
                ...question,
                // answers: [...question.answers].sort(() => Math.random() - 0.5), // Shuffle answers
            }));
        }
        return [];
    }, [lesson]);

    useEffect(() => {
        if (shuffledQuestions.length === 0 && memoizedShuffledQuestions.length > 0) {
            setShuffledQuestions(memoizedShuffledQuestions);
        }
    }, [memoizedShuffledQuestions, shuffledQuestions.length]);

    useEffect(() => {
        if (shuffledQuestions.length > 0) {
            // Filter out the questions that were previously answered correctly
            const filteredQuestions = shuffledQuestions.filter((question) => {
                return !previousAttemptAnswers[question.id]?.isCorrect;
            });
            if (filteredQuestions.length !== 0) {
                setShuffledQuestions(filteredQuestions);
            } else {
                setShuffledQuestions(lesson.questions);
            }
        }
    }, [previousAttemptAnswers]);

    useEffect(() => {
        if (data.attributes.progress) {
            const _data = data.attributes.progress[`${lesson.__typename}${lesson.id}`];
            const progress = _data?.progress;
            const retryAttempts = _data?.retryAttempts?.length ?? 0;
            const previousAttempt = _data?.retryAttempts?.[retryAttempts - 1];
            const states = {};
            const answers = {};
            const previousAttemptAnswers = {};
            lesson?.questions.forEach((question) => {
                if (!!progress?.[question.id]?.answer) {
                    states[question.id] = {
                        answered: !!progress?.[question.id]?.answer,
                        submitted: true,
                    };
                    answers[question.id] = {
                        answer: progress?.[question.id]?.answer,
                    };
                }
                previousAttemptAnswers[question.id] = {
                    answer: previousAttempt?.[question.id]?.answer,
                    isCorrect: previousAttempt?.[question.id]?.isCorrect,
                };
            });
            setQuestionStates(states);
            setQuizAnswers(answers);
            setPreviousAttemptAnswers(previousAttemptAnswers);
            const answerCount = Object.keys(answers).length ?? 0;
            setActiveQuestion(answerCount === 0 ? 0 : answerCount - 1);
            setQuizCompleted(_data?.completed);
        }
    }, [data.attributes.progress, lesson]);

    const onQuestionPrev = () => {
        if (activeQuestion === 0) return;
        setActiveQuestion(activeQuestion - 1);
    };

    const onQuestionNext = () => {
        if (quizCompleted) {
            onNext();
        }
        if (activeQuestion === shuffledQuestions.length) {
            setQuizCompleted(true);
        } else {
            setActiveQuestion(activeQuestion + 1);
        }
    };

    const onQuizAnswer = (questionId, e) => {
        setQuizAnswers({
            ...quizAnswers,
            [questionId]: {
                ...quizAnswers[questionId],
                answer: [e.target.value],
            },
        });
        setQuestionStates({
            ...questionStates,
            [questionId]: {
                ...questionStates[questionId],
                answered: true,
            },
        });
    };

    const onMultipleQuizAnswer = (questionId, event) => {
        const selectedAnswer = event.target.value;
        const isChecked = event.target.checked;

        let answer = [];
        if (quizAnswers[questionId]?.answer) answer = [...quizAnswers[questionId]?.answer];

        if (isChecked) {
            answer.push(selectedAnswer);
        } else {
            answer = answer.filter((a) => a !== selectedAnswer);
        }

        setQuizAnswers({
            ...quizAnswers,
            [questionId]: {
                ...quizAnswers[questionId],
                answer: answer,
            },
        });
        setQuestionStates({
            ...questionStates,
            [questionId]: {
                ...questionStates[questionId],
                answered: answer.length > 0,
            },
        });
    };

    const onSubmitQuestion = (questionId) => {
        // go to next question
        // onQuestionNext();
        setQuestionStates({
            ...questionStates,
            [questionId]: {
                ...questionStates[questionId],
                submitted: true,
            },
        });

        let progress = data.attributes.progress?.[`${lesson.__typename}${lesson.id}`]?.progress ?? {};

        const stats = {
            totalQuestions: shuffledQuestions.length,
            correct: 0,
            wrong: 0,
            totalPoints: lesson?.questions.reduce((acc, q) => acc + q.points, 0),
            points: 0,
            ...data.attributes.progress?.[`${lesson.__typename}${lesson.id}`]?.stats,
        };

        const question = shuffledQuestions[activeQuestion];

        const hasMultipleAnswers = isMultipleAnswer(question);
        if (hasMultipleAnswers) {
            const answers = quizAnswers[question.id]?.answer;
            const correctAnswers = question.answers.filter((a) => a.Correct).map((a) => a.id);
            const hasSelectedAllCorrectAnswers =
                answers.length === correctAnswers.length &&
                answers.every((answer) => correctAnswers.includes(answer)) &&
                correctAnswers.every((correctAnswer) => answers.includes(correctAnswer));

            // stats.totalPoints += question.points;
            if (hasSelectedAllCorrectAnswers) {
                stats.correct += 1;
                stats.points += question.points;
            } else {
                stats.wrong += 1;
            }

            progress = {
                ...progress,
                [question.id]: {
                    ...progress[question.id],
                    answer: quizAnswers[question.id]?.answer,
                    isCorrect: hasSelectedAllCorrectAnswers,
                    correct: hasSelectedAllCorrectAnswers ? answers : null,
                },
            };

            stats.percentage = Math.ceil((stats.points / stats.totalPoints) * 100);
        } else {
            const answer = quizAnswers[question.id]?.answer[0];
            const isSelectedAnswerCorrect = question.answers.find((a) => a.id === answer)?.Correct;

            // stats.totalPoints += question.points;
            if (isSelectedAnswerCorrect) {
                stats.correct += 1;
                stats.points += question.points;
            } else {
                stats.wrong += 1;
            }
            progress = {
                ...progress,
                [question.id]: {
                    ...progress[question.id],
                    answer: quizAnswers[question.id]?.answer,
                    isCorrect: isSelectedAnswerCorrect,
                    correct: isSelectedAnswerCorrect ? answer : null,
                },
            };
            stats.percentage = Math.ceil((stats.points / stats.totalPoints) * 100);
        }

        const isLastQuestion = activeQuestion === lesson?.questions.length;
        if (isLastQuestion) {
            setQuizCompleted(true);
        }

        // Save to backend
        dispatch(
            saveProgressAsync({
                id,
                data: {
                    progress: {
                        ...(data.attributes.progress ?? {}),
                        [`${lesson.__typename}${lesson.id}`]: {
                            ...data.attributes.progress?.[`${lesson.__typename}${lesson.id}`],
                            progress,
                            stats,
                            completed: isLastQuestion,
                        },
                    },
                },
            })
        );
    };

    const onRetryQuiz = () => {
        const _progress = data.attributes.progress?.[`${lesson.__typename}${lesson.id}`]?.progress;
        const isFullyCorrect = Object.values(_progress).every((q) => q.isCorrect);
        const progress = {};
        const stats = {
            totalQuestions: lesson?.questions.length,
            correct: 0,
            wrong: 0,
            totalPoints: lesson?.questions.reduce((acc, q) => acc + q.points, 0),
            points: 0,
            ...(!isFullyCorrect ? _progress.stats : {}),
        };
        const keys = Object.keys(_progress);
        keys.forEach((k) => {
            const _q = _progress[k];
            if (_q.isCorrect && !isFullyCorrect) {
                // progress[k] = _q;
                stats.correct += 1;
                stats.points += lesson.questions.find((q) => q.id === k).points;
            }
        });

        dispatch(
            saveProgressAsync({
                id,
                data: {
                    progress: {
                        ...(data.attributes.progress ?? {}),
                        [`${lesson.__typename}${lesson.id}`]: {
                            ...data.attributes.progress?.[`${lesson.__typename}${lesson.id}`],
                            progress,
                            retryAttempts: [
                                ...(data.attributes.progress?.[`${lesson.__typename}${lesson.id}`].retryAttempts ?? []),
                                data.attributes.progress?.[`${lesson.__typename}${lesson.id}`]?.progress,
                            ],
                            stats,
                            completed: false,
                        },
                    },
                },
            })
        );

        regenerateShuffleKey(true);
    };

    const retryAttempts = data.attributes.progress?.[`${lesson.__typename}${lesson.id}`]?.retryAttempts || [];
    const retryCount = retryAttempts.length;
    const hasMaxRetries = retryCount >= (lesson.maxRetryAttempts || Infinity);
    const stats = data.attributes.progress?.[`${lesson.__typename}${lesson.id}`]?.stats || {};
    const hasPassedQuiz = (stats.percentage || 0) >= (lesson.passingThreshhold || 0);
    const isLastQuestion = activeQuestion === (shuffledQuestions.length || 0) - 1;
    const questionId = shuffledQuestions[activeQuestion]?.id;
    const questionState = questionStates[questionId] || {};
    const showNext = questionState.submitted && !(isLastQuestion && !hasPassedQuiz && !hasMaxRetries);
    const showRetry = questionState.submitted && isLastQuestion && !hasMaxRetries;

    const generateButtons = () => {
        const actions = [];
        if (activeQuestion !== 0) {
            actions.push(
                <Button shape="round" size="small" onClick={onQuestionPrev}>
                    <Left /> Prev
                </Button>
            );
        }

        //if not submitted show submit
        if (!questionStates[questionId]?.submitted) {
            const hasSelectedAnswer = quizAnswers[questionId]?.answer;
            actions.push(
                <Button
                    key="submit-btn"
                    shape="round"
                    size="small"
                    onClick={() => onSubmitQuestion(questionId)}
                    disabled={!hasSelectedAnswer}
                >
                    Submit
                </Button>
            );
        }
        const showResults = questionStates[questionId]?.submitted && isLastQuestion;

        //if all submitted show next
        if (!showResults && questionStates[questionId]?.submitted && !(isLastQuestion && !hasPassedQuiz && !hasMaxRetries)) {
            actions.push(
                <Button key="next-btn" shape="round" size="small" onClick={onQuestionNext}>
                    Next <Right />
                </Button>
            );
        }
        if (showResults) {
            actions.push(
                <Button key="next-btn" shape="round" size="small" onClick={() => setQuizCompleted(true)}>
                    View Results <Right />
                </Button>
            );
        }

        return actions;
    };

    const handleAutoNavigate = () => {
        if (isLastQuestion) {
            setQuizCompleted(true);
        } else {
            onQuestionNext();
        }
    };

    useEffect(() => {
        if (showNext || showRetry) {
            setShowAutoNavigateMessage(true);
            setCountdown(5); // Reset countdown
            const interval = setInterval(() => {
                setCountdown((prevCountdown) => prevCountdown - 1);
            }, 1000);
            const timeout = setTimeout(handleAutoNavigate, 5000);
            setAutoNavigateTimeout(timeout);

            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
            };
        } else {
            setShowAutoNavigateMessage(false);
            if (autoNavigateTimeout) {
                clearTimeout(autoNavigateTimeout);
                setAutoNavigateTimeout(null);
            }
        }
    }, [showNext, showRetry]);

    const cancelAutoNavigate = () => {
        setShowAutoNavigateMessage(false);
        if (autoNavigateTimeout) {
            clearTimeout(autoNavigateTimeout);
            setAutoNavigateTimeout(null);
        }
    };

    const getAnswerState = (question, answer) => {
        const selectedAnswers = quizAnswers[question.id]?.answer || []; // is an array of selected answers
        const isSelected = selectedAnswers.includes(answer.id);
        const isCorrect = question.answers.find((a) => a.id === answer.id)?.Correct;
        const isPreviousAttemptAnswer = quizAnswers[question.id]?.isPreviousAnswerCorrect;

        const isSubmitted = questionStates[question.id]?.submitted;

        if (isPreviousAttemptAnswer && isCorrect) {
            return "correct";
        }

        if (isSelected && isSubmitted) {
            return isCorrect ? "correct" : "wrong";
        }

        return "";
    };

    const isMultipleAnswer = (question) => question?.answers?.filter((a) => a.Correct).length > 1;

    const handleTimeout = () => {
        // setTimeout(true);
    };
    const isSelectedAnswerOrAnswersCorrect = useMemo(() => {
        return (question) => {
            const selectedAnswers = quizAnswers[question.id]?.answer || [];
            const correctAnswers = question.answers.filter((a) => a.Correct).map((a) => a.id);

            return selectedAnswers.length === correctAnswers.length && selectedAnswers.every((answer) => correctAnswers.includes(answer));
        };
    }, [quizAnswers]);

    const getMergedResultsWithPreviousAttempt = useMemo(() => {
        const results = data?.attributes?.progress?.[`${lesson.__typename}${lesson.id}`];
        const retryAttempts = results?.retryAttempts || [];
        const previousAttempt = retryAttempts.length > 0 ? retryAttempts[retryAttempts.length - 1] : null;
        const mergedResults = {};
        const stats = {
            points: 0,
            totalPoints: 0,
            totalQuestions: 0,
            correct: 0,
            wrong: 0,
            percentage: 0,
        };

        lesson?.questions.forEach((question) => {
            const currentAnswer = quizAnswers[question.id]?.answer || [];
            const currentIsCorrect = isSelectedAnswerOrAnswersCorrect(question);

            // Check if the question is in the current shuffledQuestions list
            const isInCurrentAttempt = shuffledQuestions.some((q) => q.id === question.id);

            let previousAnswer = [];
            let previousIsCorrect = false;

            if (!isInCurrentAttempt && previousAttempt) {
                previousAnswer = previousAttempt[question.id]?.answer || [];
                previousIsCorrect = previousAttempt[question.id]?.isCorrect || false;
            }

            // Determine if the question is correct in either the current or previous attempts
            const isCorrect = currentIsCorrect || previousIsCorrect;

            mergedResults[question.id] = {
                answer: currentAnswer.length > 0 ? currentAnswer : previousAnswer,
                isCorrect,
            };

            // Update stats
            stats.totalQuestions += 1;
            stats.totalPoints += question.points;
            if (isCorrect) {
                stats.correct += 1;
                stats.points += question.points;
            } else {
                stats.wrong += 1;
            }
        });

        stats.percentage = ((stats.points / stats.totalPoints) * 100).toFixed(0);

        return { progress: mergedResults, stats };
    }, [lesson, quizAnswers, data, shuffledQuestions]);

    return (
        <div>
            <h2>{lesson?.title}</h2>
            <WysiwygDisplay data={lesson?.description} enableAudio={false} handleTimeout={handleTimeout} />
            {quizCompleted ? (
                <>
                    <CourseResult
                        results={getMergedResultsWithPreviousAttempt}
                        questions={lesson?.questions}
                        onNext={showNext ? onQuestionNext : null}
                        onRetry={showRetry ? onRetryQuiz : null}
                    />
                </>
            ) : (
                <>
                    <>
                        <Row>
                            <Col span={(lesson?.questions[activeQuestion]?.hint && "23") || "24"}>
                                <TextToSpeech text={shuffledQuestions[activeQuestion]?.title} />
                                <h3>{shuffledQuestions[activeQuestion]?.title}</h3>
                            </Col>
                            {shuffledQuestions[activeQuestion]?.hint && (
                                <Col
                                    span={1}
                                    style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", paddingTop: "11px" }}
                                >
                                    <Tooltip placement="bottom" title={shuffledQuestions[activeQuestion]?.hint} className="QuizHint">
                                        <QuestionCircleOutlined />
                                    </Tooltip>
                                </Col>
                            )}
                        </Row>
                        <WysiwygDisplay data={shuffledQuestions[activeQuestion]?.description} enableAudio={false} />
                        {isMultipleAnswer(shuffledQuestions[activeQuestion]) ? (
                            <Checkbox.Group
                                value={quizAnswers[shuffledQuestions[activeQuestion]?.id]?.answer}
                                disabled={questionStates[shuffledQuestions[activeQuestion]?.id]?.submitted}
                                key={shuffledQuestions[activeQuestion]?.id}
                            >
                                <Space direction="vertical">
                                    {shuffledQuestions[activeQuestion]?.answers.map((answer) => (
                                        <>
                                            <Checkbox
                                                key={answer.id}
                                                className={`${getAnswerState(shuffledQuestions[activeQuestion], answer)}`}
                                                value={answer.id}
                                                onChange={(e) => onMultipleQuizAnswer(shuffledQuestions[activeQuestion]?.id, e)}
                                            >
                                                {answer.Answer}
                                            </Checkbox>
                                        </>
                                    ))}
                                </Space>
                            </Checkbox.Group>
                        ) : (
                            <Radio.Group
                                value={quizAnswers[shuffledQuestions[activeQuestion]?.id]?.answer?.[0]}
                                onChange={(e) => onQuizAnswer(shuffledQuestions[activeQuestion]?.id, e)}
                                disabled={questionStates[shuffledQuestions[activeQuestion]?.id]?.submitted}
                                key={shuffledQuestions[activeQuestion]?.id}
                            >
                                <Space direction="vertical">
                                    {shuffledQuestions[activeQuestion]?.answers.map((answer) => (
                                        <>
                                            <Radio
                                                key={answer.id}
                                                className={`${getAnswerState(shuffledQuestions[activeQuestion], answer)}`}
                                                value={answer.id}
                                            >
                                                {answer.Answer}
                                            </Radio>
                                        </>
                                    ))}
                                </Space>
                            </Radio.Group>
                        )}
                    </>

                    {questionStates[shuffledQuestions[activeQuestion]?.id]?.submitted && (
                        <Flex justify="start" align="center" gap={10} style={{ marginTop: "40px" }}>
                            {isSelectedAnswerOrAnswersCorrect(shuffledQuestions[activeQuestion]) ? (
                                <Alert message="You have answered correctly!" type="success" />
                            ) : (
                                <Alert message="You have answered incorrectly!" type="error" />
                            )}
                        </Flex>
                    )}

                    <Flex justify="start" align="center" gap={10} style={{ marginTop: "40px" }}>
                        {generateButtons()}
                        {showAutoNavigateMessage && (
                            <Button key="submit-btn" shape="round" size="small" onClick={cancelAutoNavigate}>
                                Loading next in {countdown} seconds - click to cancel.
                            </Button>
                        )}
                    </Flex>
                </>
            )}
        </div>
    );
};
